import { randomUuidV4 } from "../utils/randomUuidV4";
import axios from "axios";
import "../store";
import config from "../config";
import authHeader from "./auth-header";
import { withBookingReadRetry } from "../utils/bookingReadRetry";
import { resourceAllocationChoices } from "../helpers/resourceAllocations";

const API_URL = config.API_URL;

const IDEMPOTENCY_INPUT_FIELDS = new Set([
  "idempotency_key",
  "idempotencyKey",
]);
const TRANSIENT_FINGERPRINT_FIELDS = new Set([
  ...IDEMPOTENCY_INPUT_FIELDS,
  "paymentIntent",
  "payment_token",
  "stripe_token_id",
]);
const pendingGeneratedIdempotencyKeys = new Map();

const normalizeForFingerprint = (value, seen = new Set()) => {
  if (value === null || typeof value !== "object") {
    if (typeof value === "bigint") return value.toString();
    if (typeof value === "undefined" || typeof value === "function" || typeof value === "symbol") return null;
    return value;
  }
  if (value instanceof Date) return value.toISOString();
  if (seen.has(value)) return "[Circular]";
  seen.add(value);

  let normalized;
  if (Array.isArray(value)) {
    normalized = value.map((item) => normalizeForFingerprint(item, seen));
  } else {
    normalized = {};
    Object.keys(value)
      .filter((key) => !TRANSIENT_FINGERPRINT_FIELDS.has(key))
      .sort()
      .forEach((key) => {
        const item = value[key];
        if (typeof item !== "undefined" && typeof item !== "function" && typeof item !== "symbol") {
          normalized[key] = normalizeForFingerprint(item, seen);
        }
      });
  }
  seen.delete(value);
  return normalized;
};

const logicalRequestFingerprint = (values) => {
  const normalized = normalizeForFingerprint(values);
  return JSON.stringify(normalized) || String(normalized);
};

const create = (values) => {
  const formData = new FormData();
  for (let value in values || {}) {
    if (IDEMPOTENCY_INPUT_FIELDS.has(value)) continue;
    if (["BookingSelectedServicesData", "BookingSelectedMembershipData", "paymentIntent","BookingSelectedSubscriptionData"].includes(value) && values[value] && typeof values[value] === "object") {
      const serialized = value === "BookingSelectedServicesData"
        ? values[value].map((service) => ({ ...service, resource_allocations: resourceAllocationChoices(service.resource_allocations) }))
        : values[value];
      formData.append(value, JSON.stringify(serialized));
    } else {
      formData.append(value, values[value]);
    }
  }
  const action = "widget/booking/store";
  formData.append("action", action);
  const requestedIdempotencyKey = values && (values.idempotency_key || values.idempotencyKey);
  const explicitIdempotencyKey = typeof requestedIdempotencyKey === "string" && requestedIdempotencyKey.trim()
    ? requestedIdempotencyKey
    : null;
  const fingerprint = explicitIdempotencyKey ? null : logicalRequestFingerprint(values || {});
  const idempotencyKey = explicitIdempotencyKey || pendingGeneratedIdempotencyKeys.get(fingerprint) || randomUuidV4();
  if (!explicitIdempotencyKey && !pendingGeneratedIdempotencyKeys.has(fingerprint)) {
    pendingGeneratedIdempotencyKeys.set(fingerprint, idempotencyKey);
  }
  const request = axios.post(API_URL + action, formData, {
    headers: {
      ...authHeader({ contentType: "multipart/form-data" }),
      "Idempotency-Key": idempotencyKey,
    },
  });
  if (explicitIdempotencyKey) return request;
  return Promise.resolve(request).then((response) => {
    pendingGeneratedIdempotencyKeys.delete(fingerprint);
    return response;
  });
};

const view = (values) => {
  const result = values && values.result ? values.result : "";
  const pathname = typeof values?.pathname === "string" ? values.pathname : "";
  // The link's selection query is the canonical carrier of a public deep link,
  // so it must reach the API instead of staying client-side. Blank parameters
  // are ignored server-side and keep the stored link selection.
  const search = typeof values?.search === "string" ? values.search : "";
  const action = "widget/booking/view";
  const data = {
    action: action,
    pagination: false, //true or false
    result: result, //business_name,owner_name
    location: `${pathname}${search}`,
  };
  return withBookingReadRetry((signal) => axios.post(API_URL + action, data, { headers: authHeader(), signal }));
};


const bookingCategoryView = (values) => {
  const sort = values && values.sort;
  const page = values && values.page;
  const next_page_url = values && values.next_page_url;
  const result = values && values.result ? values.result : "";
  const fromPosition = values && values.fromPosition ? values.fromPosition : "";
  const toPosition = values && values.toPosition ? values.toPosition : "";
  const uid = values && values.uid ? values.uid : "";
  const salon_id = values && values.salon_id ? values.salon_id : "";
  let sortstring = "";
  if (sort) {
    let sortArray = [];
    Object.keys(sort).map(function (key, index) {
      return (sortArray[index] = `sort[${key}]=${sort[key]}`);
    });
    if (sortArray.length > 0) {
      let jsort = sortArray.join("&");
      sortstring = jsort;
    }
  }

  if (values) {
    if (values.id) {

    }
    if (values.option) {

    }
    if (values.pagination) {
      void (values.pagination);
    }
  }
  const action = page ? `widget/categories/view?page=${page}&${sortstring}` : `widget/categories/view?${sortstring}`;
  const data = {
    action: action,
    pagination: false, //true or false
    id: values && values.id ? values.id : "",
    result: result, //business_name,owner_name
    option: values && values.option ? values.option : "",
    fromPosition: fromPosition,
    toPosition: toPosition,
    uid: uid,
    salon_id: salon_id,
  };
  return withBookingReadRetry((signal) => axios.post(next_page_url ? `${next_page_url}&${sortstring}` : API_URL + action, data, { headers: authHeader(), signal }));
};

const bookingServiceView = (values) => {
  const sort = values && values.sort;
  const page = values && values.page;
  const next_page_url = values && values.next_page_url;
  const dropdown = values && values.dropdown ? values.dropdown : "";
  const result = values && values.result ? values.result : "";
  const fromPosition = values && values.fromPosition ? values.fromPosition : "";
  const toPosition = values && values.toPosition ? values.toPosition : "";
  const uid = values && values.uid ? values.uid : "";
  const salon_id = values && values.salon_id ? values.salon_id : "";
  const isNotServiceIds = values && values.isNotServiceIds ? values.isNotServiceIds : "";
  const primary_service_id = values && values.primary_service_id ? values.primary_service_id : "";
  const availebleTimeObjAnother = values && values.availebleTimeObjAnother ? values.availebleTimeObjAnother : "";
  const isBookingStep = values && values.isBookingStep ? values.isBookingStep : "";

  let sortstring = "";
  if (sort) {
    let sortArray = [];
    let sortSubArray = [];
    Object.keys(sort).map(function (key, index) {
      if (Object.keys(sort[key]).length > 0) {
        Object.keys(sort[key]).map(function (subkey, subindex) {
          sortSubArray[subindex] = `sort[${key}.${subkey}]=${sort[key][subkey]}`;
        });
      }
      if (key != "category") {
        sortArray[index] = `sort[${key}]=${sort[key]}`;
      }
    });
    if (sortSubArray.length > 0) {
      let jsubsort = sortSubArray.join("&");
      sortstring = jsubsort;
    }
    if (sortArray.length > 0) {
      let jsort = sortArray.join("&");
      sortstring = jsort;
    }
  }

  if (values) {
    if (values.id) {

    }
    if (values.option) {

    }
    if (values.pagination) {
      void (values.pagination);
    }
  }
  const action = page ? `widget/services/view?page=${page}&${sortstring}` : `widget/services/view?${sortstring}`;
  let servicedata;
  if (dropdown) {
    servicedata = {
      action: action,
      pagination: false, //true or false
      id: "",
      // field: "name,description,duration,padding_time,color,service_booked_online,deposit_booked_online,deposit_booked_price", // first_name,last_name,email
      result: result, //business_name,owner_name
      uid: uid,
      salon_id: salon_id,
      isNotServiceIds: isNotServiceIds,
      primary_service_id: primary_service_id
    };
  } else {
    servicedata = {
      action: action,
      pagination: false, //true or false
      id: values && values.id ? values.id : "",
      // field: values && values.id ? "" : "name,description,duration,padding_time,color,service_booked_online,deposit_booked_online,deposit_booked_price", // first_name,last_name,email
      result: result, //business_name,owner_name
      option: values && values.option ? values.option : "",
      category_id: values && values.category_id ? values.category_id : "",
      fromPosition: fromPosition,
      toPosition: toPosition,
      uid: uid,
      salon_id: salon_id,
      isNotServiceIds: isNotServiceIds,
      primary_service_id: primary_service_id,
      availebleTimeObjAnother:availebleTimeObjAnother,
      isBookingStep:isBookingStep
    };
  }

  const data = servicedata;
  return withBookingReadRetry((signal) => axios.post(next_page_url ? `${next_page_url}&${sortstring}` : API_URL + action, data, { headers: authHeader(), signal }));
};

const bookingStaffView = (values) => {
  void (values && values.sort);
  const page = values && values.page;
  const next_page_url = values && values.next_page_url;
  const result = values && values.result ? values.result : "";
  const service_id = values && values.service_id ? values.service_id : "";
  const uid = values && values.uid ? values.uid : "";
  const salon_id = values && values.salon_id ? values.salon_id : "";
  const availebleTimeObjAnother = values && values.availebleTimeObjAnother ? values.availebleTimeObjAnother : "";
  const isBookingStep = values && values.isBookingStep ? values.isBookingStep : "";
  const formType = "bookingwidget/staff/view";


  if (values) {
    if (values.id) {

    }
    if (values.option) {

    }
    if (values.pagination) {
      void (values.pagination);
    }
  }
  const action = page ? `widget/staff/view?page=${page}` : `widget/staff/view`;

  const data = {
    action: action,
    pagination: false, //true or false
    id: values && values.id ? values.id : "",
    result: result, //business_name,owner_name
    option: values && values.option ? values.option : "",
    service_id: service_id,
    uid: uid,
    salon_id: salon_id,
    availebleTimeObjAnother:availebleTimeObjAnother,
    isBookingStep:isBookingStep,
    formType:formType
  };
  return withBookingReadRetry((signal) => axios.post(next_page_url ? `${next_page_url}` : API_URL + action, data, { headers: authHeader(), signal }));
};

const bookingStaffAvailableTime = (values) => {
  const service_id = values && values.service_id ? values.service_id : "";
  const staff_id = values && values.staff_id ? values.staff_id : "";
  const dateof = values && values.dateof ? values.dateof : "";
  const page_type = values && values.page_type ? values.page_type : "";
  const uid = values && values.uid ? values.uid : "";
  const salon_id = values && values.salon_id ? values.salon_id : "";
  const listview = values && values.listview ? values.listview : "";
  // const serviceObjData = values && values.serviceObjData ? values.serviceObjData : "";
  void (values && values.first_match ? values.first_match : "");
  // const staffObjData = values && values.staffObjData ? values.staffObjData : "";
  // const duration = serviceObjData && serviceObjData.duration ? serviceObjData.duration : "";
  // const padding_time = serviceObjData && serviceObjData.padding_time ? serviceObjData.padding_time : "";

  const duration = values && values.duration ? values.duration : "";
  const padding_time = values && values.padding_time ? values.padding_time : "";
  const durationWithPadding = values && values.durationWithPadding ? values.durationWithPadding : "";
  const parentAppointmentEndTime = values && values.parentAppointmentEndTime ? values.parentAppointmentEndTime : "";

  const isBookingSelectedServicesData = values.isBookingSelectedServicesData ? values.isBookingSelectedServicesData : "";
  const BookingSelectedServicesData =
    isBookingSelectedServicesData &&
    isBookingSelectedServicesData.filter((item) => {
      if (item.category_id != "" && item.service_id != "" && item.staff_id != "" && item.datetime != "") {
        return item;
      }
    });
  const action = `widget/staff/availabletime`;

  const data = {
    action: action,
    service_id: service_id,
    staff_id: staff_id,
    dateof: dateof,
    uid: uid,
    salon_id: salon_id,
    duration: duration,
    padding_time: padding_time,
    durationWithPadding: durationWithPadding,
    parentAppointmentEndTime: parentAppointmentEndTime,
    BookingSelectedServicesData: BookingSelectedServicesData,
    // first_match:first_match,
    listview: listview,
    page_type: page_type
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
};
const checkStaffAvailableTime = (values) => {
  const service_id = values && values.service_id ? values.service_id : "";
  const staff_id = values && values.staff_id ? values.staff_id : "";
  const dateof = values && values.dateof ? values.dateof : "";
  const page_type = values && values.page_type ? values.page_type : "";
  const uid = values && values.uid ? values.uid : "";
  const salon_id = values && values.salon_id ? values.salon_id : "";
  const serviceObjData = values && values.serviceObjData ? values.serviceObjData : "";
  void (values && values.first_match ? values.first_match : "");
  // const staffObjData = values && values.staffObjData ? values.staffObjData : "";
  const duration = serviceObjData && serviceObjData.duration ? serviceObjData.duration : "";
  const padding_time = serviceObjData && serviceObjData.padding_time ? serviceObjData.padding_time : "";
  const BookingSelectedServicesData = values.BookingSelectedServicesData ? values.BookingSelectedServicesData : "";
  // const BookingSelectedServicesData =
  //   isBookingSelectedServicesData &&
  //   isBookingSelectedServicesData.filter((item) => {
  //     if (item.category_id != "" && item.service_id != "" && item.staff_id != "" && item.datetime != "") {
  //       return item;
  //     }
  //   });
  const action = `widget/booking/availableappointment`;

  const data = {
    action: action,
    service_id: service_id,
    staff_id: staff_id,
    dateof: dateof,
    uid: uid,
    salon_id: salon_id,
    duration: duration,
    padding_time: padding_time,
    BookingSelectedServicesData: BookingSelectedServicesData,
    // first_match:first_match,
    page_type: page_type
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const createPaymentIntent = (values) => {
  // console.log("apivalue", values);
  const action = `widget/booking/createpaymentintent`;

  const data = { uid: values?.uid, sale_id: values?.sale_id, action };
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const bookingpayment = (values) => {
  const formData = new FormData();
  for (let value in values) {
    if (["paymentIntent"].includes(value) && values[value] && typeof values[value] === "object") {
      formData.append(value, JSON.stringify(values[value]));
    } else {
      formData.append(value, values[value]);
    }
  }
  const action = `widget/booking/bookingpayment`;
  formData.append("action", action);
  return axios.post(API_URL + action, formData, { headers: authHeader() });
};

const bookingpaymentfailed = (values) => {
  const formData = new FormData();
  for (let value in values) {
    if (["error", 'paymentIntent'].includes(value) && values[value] && typeof values[value] === "object") {
      formData.append(value, JSON.stringify(values[value]));
    } else {
      formData.append(value, values[value]);
    }
  }
  const action = `widget/booking/bookingpaymentfailed`;
  formData.append("action", action);
  return axios.post(API_URL + action, formData, { headers: authHeader() });
};

const bookingConfirmation = ({ uid, token }) => {
  const encodedToken = encodeURIComponent(token);
  return axios.get(`${API_URL}widget/booking/confirmation/${encodedToken}`, {
    params: { uid },
  });
};

const purchaseConfirmation = ({ uid, token }) =>
  axios.get(`${API_URL}widget/purchase/confirmation/${encodeURIComponent(token)}`, {
    params: { uid },
  });

const bookingMembershipView = (values) => {
  const page = values && values.page;
  const next_page_url = values && values.next_page_url;
  const result = values && values.result ? values.result : "";
  const membership_id = values && values.membership_id ? values.membership_id : "";
  const uid = values && values.uid ? values.uid : "";
  const salon_id = values && values.salon_id ? values.salon_id : "";


  if (values) {
    if (values.id) {

    }
    if (values.option) {

    }
    if (values.pagination) {
      void (values.pagination);
    }
  }
  const action = page ? `widget/membership/view?page=${page}` : `widget/membership/view`;
  const data = {
    action: action,
    pagination: false, //true or false
    id: values && values.id ? values.id : "",
    result: result, //business_name,owner_name
    option: values && values.option ? values.option : "",
    membership_id: membership_id,
    uid: uid,
    salon_id: salon_id,
  };
  return axios.post(next_page_url ? `${next_page_url}` : API_URL + action, data, { headers: authHeader() });
};

const bookingSubscriptionView = (values) => {
  const page = values && values.page;
  const next_page_url = values && values.next_page_url;
  const result = values && values.result ? values.result : "";
  const membership_id = values && values.membership_id ? values.membership_id : "";
  const uid = values && values.uid ? values.uid : "";
  const salon_id = values && values.salon_id ? values.salon_id : "";


  if (values) {
    if (values.id) {

    }
    if (values.option) {

    }
    if (values.pagination) {
      void (values.pagination);
    }
  }
  const action = page ? `widget/subscription/view?page=${page}` : `widget/subscription/view`;
  const data = {
    action: action,
    pagination: false, //true or false
    id: values && values.id ? values.id : "",
    result: result, //business_name,owner_name
    option: values && values.option ? values.option : "",
    membership_id: membership_id,
    uid: uid,
    salon_id: salon_id,
  };
  return axios.post(next_page_url ? `${next_page_url}` : API_URL + action, data, { headers: authHeader() });
};

const bookingVocuherView = (values) => {
  const page = values && values.page;
  const next_page_url = values && values.next_page_url;
  const result = values && values.result ? values.result : "";
  const membership_id = values && values.membership_id ? values.membership_id : "";
  const uid = values && values.uid ? values.uid : "";
  const salon_id = values && values.salon_id ? values.salon_id : "";


  if (values) {
    if (values.id) {

    }
    if (values.option) {

    }
    if (values.pagination) {
      void (values.pagination);
    }
  }
  const action = page ? `widget/voucher/view?page=${page}` : `widget/voucher/view`;
  const data = {
    action: action,
    pagination: false, //true or false
    id: values && values.id ? values.id : "",
    result: result, //business_name,owner_name
    option: values && values.option ? values.option : "",
    voucher_type: values && values.voucher_type ? values.voucher_type : "",
    membership_id: membership_id,
    uid: uid,
    salon_id: salon_id,
  };
  return axios.post(next_page_url ? `${next_page_url}` : API_URL + action, data, { headers: authHeader() });
};

const bookingVoucherWithServiceView = (values) => {
  const page = values && values.page;
  const next_page_url = values && values.next_page_url;
  const result = values && values.result ? values.result : "";
  const membership_id = values && values.membership_id ? values.membership_id : "";
  const uid = values && values.uid ? values.uid : "";
  const salon_id = values && values.salon_id ? values.salon_id : "";


  if (values) {
    if (values.id) {

    }
    if (values.option) {

    }
    if (values.pagination) {
      void (values.pagination);
    }
  }
  const action = page ? `widget/voucher/view?page=${page}` : `widget/voucher/view`;
  const data = {
    action: action,
    pagination: false, //true or false
    id: values && values.id ? values.id : "",
    result: result, //business_name,owner_name
    option: values && values.option ? values.option : "",
    voucher_type: values && values.voucher_type ? values.voucher_type : "Service",
    membership_id: membership_id,
    voucher_online : values && values.voucher_online ? values.voucher_online : "",
    uid: uid,
    salon_id: salon_id,
  };
  return axios.post(next_page_url ? `${next_page_url}` : API_URL + action, data, { headers: authHeader() });
};

const firstavailabletimebookinglink = (values) => {
  const formType = "bookingwidget/staff/view";
  const action = `widget/booking/nextresourceitemtime`;

  const data = {
    ...values,
    formType,formType
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const nextresourceitemtimeget = (values) => {
  const formType = "bookingwidget/staff/view"
  const action = "widget/booking/nextresourceitemtime";

  const data = {
    ...values,
    formType:formType
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const recourceitems = (values) => {

  const action = "widget/booking/bookingresourceitem";

  const data = {
    ...values,
    action
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const disabledates = (values) => {
  const action = "widget/booking/disabledates";
  const data = {
    ...values,
    action
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
}

const anyavailablestaff = (values) => {
  const formType = "bookingwidget/staff/view"
  const action = "widget/booking/anyavailablestaff";

  const data = {
    ...values,
    formType:formType
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const checkStaffAvailability = (values) => {
  const treatments = values?.categories?.flatMap((category) => (
    Array.isArray(category?.treatments) ? category.treatments : []
  )) || [];
  const hasStaffPreference = treatments.length > 0 && treatments.every((treatment) => (
    treatment?.staff_id !== null &&
    treatment?.staff_id !== undefined &&
    treatment?.staff_id !== ""
  ));
  const action = hasStaffPreference
    ? "widget/booking/staff-availability"
    : "widget/booking/staff-availability-no-preference";
  const data = {
    id: values?.id || "",
    category_id: values?.category_id || "",
    service_id: values?.service_id || "",
    staff_id: values?.staff_id || "",
    serviceObjData: values?.serviceObjData || null,
    staffObjData: values?.staffObjData || null,
    datetime: values?.datetime || "",
    totalprice: values?.totalprice || 0,
    duration: values?.duration || "",
    padding_time: values?.padding_time || "",
    durationWithPadding: values?.durationWithPadding || "",
    parentAppointmentEndTime: values?.parentAppointmentEndTime || "",
    resource_item_id: values?.resource_item_id || "",
    resource_name: values?.resource_name || "",
    appointment_id: values?.appointment_id || "",
    busytime_id: values?.busytime_id || "",
    dateof: values?.dateof || "",
    start_datetime: values?.start_datetime || "",
    listview: values?.listview || "",
    salon_id: values?.salon_id || "",
    uid: values?.uid || "",
    page_type: values?.page_type || "",
    formType: values?.formType || "bookingwidget/staff/view",
    availability_mode: values?.availability_mode || "",
    categories: values?.categories || []
  };
  return withBookingReadRetry((signal) => axios.post(API_URL + action, data, { headers: authHeader(), signal }));
};

const validateBookingDiscountCode = (values) => {
  const data = {
    uid: values?.uid || "",
    code: values?.code || "",
    salon_id: values?.salon_id || "",
    services_id: values?.services_id || [],
  };
  return axios.post(API_URL + "widget/booking/validate-discount-code", data, { headers: authHeader() });
};

const getBookingSummary = (values) => {

  const data = {
    services: values.services,
    date: values.date,
    start_time: values.start_time,
    end_time: values.end_time,
    salon_id: values.salon_id,
    uid: values.uid,
    // selected_staff: values.selected_staff,
    all_staff: values.all_staff,
    // resource_ids: values?.resource_ids,

  };
  return withBookingReadRetry((signal) => axios.post(API_URL + "widget/booking/booking-summary", data, { headers: authHeader(), signal }));
};

const bookingIncompatibilities = (values) => {
  const data = {
    uid: values?.uid || "",
    salon_id: values?.salon_id || "",
  };
  return axios.post(API_URL + "widget/services/incompatibilities", data, { headers: authHeader() });
};

const serviceAddOns = (values) => {
  const data = {
    uid: values?.uid || "",
    salon_id: values?.salon_id || "",
    service_id: values?.service_id || "",
  };
  return withBookingReadRetry((signal) => axios.post(API_URL + "widget/booking/service-addons", data, { headers: authHeader(), signal }));
};

// SOF-254: group ("party") booking. The browser posts service/staff/identity
// selections only -- the salon prices the party and creates one sale.
const groupAvailability = (values) => {
  const data = {
    uid: values?.uid || "",
    salon_id: values?.salon_id || "",
    ...(values?.booking_mode ? { booking_mode: values.booking_mode } : {}),
    ...(values?.month ? { month: values.month } : {}),
    local_date: values?.local_date || "",
    members: values?.members || [],
  };
  return axios.post(API_URL + "widget/booking/group-availability", data, { headers: authHeader() });
};

const groupQuote = (values) => axios.post(API_URL + "widget/booking/group-quote", {
  uid: values.uid, booking_mode: values.booking_mode,
  members: values.members, start_datetime: values.start_datetime,
}, { headers: authHeader() });

const groupStore = (values) => {
  const data = {
    uid: values?.uid || "",
    salon_id: values?.salon_id || "",
    ...(values?.booking_mode ? { booking_mode: values.booking_mode } : {}),
    booking_uid: values?.booking_uid || "",
    idempotency_key: values?.idempotency_key || "",
    start_datetime: values?.start_datetime || "",
    local_date: values?.local_date || "",
    members: values?.members || [],
    ...(Array.isArray(values?.identity_quote_tokens) && values.identity_quote_tokens.some(Boolean)
      ? { identity_quote_tokens: values.identity_quote_tokens } : {}),
  };
  return axios.post(API_URL + "widget/booking/group-store", data, { headers: authHeader() });
};

const bookingApiController = {
  create,
  view,
  bookingCategoryView,
  bookingServiceView,
  bookingStaffView,
  bookingStaffAvailableTime,
  createPaymentIntent,
  bookingpayment,
  bookingpaymentfailed,
  bookingConfirmation,
  purchaseConfirmation,
  bookingMembershipView,
  bookingVocuherView,
  bookingVoucherWithServiceView,
  firstavailabletimebookinglink,
  checkStaffAvailableTime,
  nextresourceitemtimeget,
  recourceitems,
  disabledates,
  anyavailablestaff,
  bookingSubscriptionView,
  checkStaffAvailability,
  getBookingSummary,
  validateBookingDiscountCode,
  bookingIncompatibilities,
  serviceAddOns,
  groupAvailability,
  groupQuote,
  groupStore
};
export default bookingApiController;
